/**
 * The station-volumes chip that sits on an analysis panel: a scope note that
 * says why the corridor's net differs from the panel's own net, and a button
 * that opens the station-volumes dock on this layer.
 *
 * One component, two callers. `SurfaceDifferencePanel` and
 * `DeviationVolumePanel` carried the same note-plus-button block verbatim,
 * differing only in which scope sentence they printed — which meant a copy or
 * a formatting fix landed on one panel and not the other. The `kind` prop is
 * the whole difference now.
 */
import { stationVolumeDockAtom, stationVolumeDockHeightAtom } from '@/atoms/stationVolumes.atom';
import { formatM3Row } from '@/lib/stationVolumes/format';
import type { StationSummary, StationVolumesAnalysisKind } from '@/lib/stationVolumes/types';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

export interface StationVolumesChipProps {
  layerId: string;
  /** Decides the scope sentence and the chip's test id. */
  kind: StationVolumesAnalysisKind;
  /** The run's summary block, off the layer's metadata. Cut and fill are read
   *  for the Net's PRECISION, not to print them — see below. */
  summary: Pick<StationSummary, 'cutVolumeM3' | 'fillVolumeM3' | 'netVolumeM3' | 'binCount'>;
}

export const StationVolumesChip = ({ layerId, kind, summary }: StationVolumesChipProps) => {
  const { t } = useTranslation('project');
  const setDock = useSetAtom(stationVolumeDockAtom);
  // Ruling A7: seed the height from the persisted preference, never from
  // DOCK_HEIGHT_DEFAULT — opening the dock must not stomp the user's drag.
  const dockHeight = useAtomValue(stationVolumeDockHeightAtom);
  const deviation = kind === 'deviation';

  return (
    <>
      {/* Two "net" figures sit on the host panel once a station run exists, and
          they legitimately differ: the panel's KPIs cover the whole analysis
          grid, the chip covers the corridor only — and on a deviation run the
          two also carry opposite signs. Unlabelled, a reader takes one of them
          for wrong. */}
      <p className='mt-2 text-[11px] leading-snug text-slate-500 dark:text-stone-400'>
        {t(deviation ? 'stationVolumes.panel.gridScopeNoteDeviation' : 'stationVolumes.panel.gridScopeNote')}
      </p>
      <button
        type='button'
        data-testid={deviation ? 'dev-station-volumes-chip' : 'sd-station-volumes-chip'}
        title={t('stationVolumes.panel.chipOpen')}
        className='mt-2 flex w-full flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 rounded border border-slate-200 px-2 py-1 text-left hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:border-stone-700 dark:hover:bg-stone-800'
        onClick={() => setDock({ layerId, tab: 'chart', height: dockHeight })}>
        <span className='font-semibold'>{t('stationVolumes.panel.chipTitle')}</span>
        <span className='tabular-nums text-slate-600 dark:text-stone-300'>
          {/* `formatM3Row`, not `formatM3` and certainly not `toFixed(0)`: the
              chip and the dock's hero Net are the same figure, so they have to
              be the same STRING. The dock pools its decimals over the whole
              Cut / Fill / Net row (`StationVolumeDock`), which is why the row
              is passed here and only its net printed — the chip alone would
              give a −49.5 m³ net beside a 4 200 m³ cut three decimals of
              accuracy the row does not claim. */}
          {t('stationVolumes.panel.chip', {
            cut: formatM3Row([summary.cutVolumeM3, summary.fillVolumeM3])[0],
            fill: formatM3Row([summary.cutVolumeM3, summary.fillVolumeM3])[1],
            bins: summary.binCount,
          })}
        </span>
      </button>
    </>
  );
};
